import { FeeStructure } from '../models/FeeStructure';

// Exchange enum mapping - matches Rust Exchange enum order
export const EXCHANGES = [
  'Upbit',    // 0
  'Bithumb',  // 1
  'Coinone',  // 2
  'Binance',  // 3
  'HTX',      // 4
  'GateIO',   // 5
  'KuCoin',   // 6
  'OKX',      // 7
  'MEXC',     // 8
  'Bybit',    // 9
  'Bitget',   // 10
  // 'Coinbase'  // 11 - Not supported yet
] as const;

export type Exchange = typeof EXCHANGES[number];

// Quote currency mapping
export const QUOTE_CURRENCIES = {
  'K': 'KRW',
  'U': 'USDT',
  'B': 'BTC'
} as const;

export type QuoteChar = keyof typeof QUOTE_CURRENCIES;
export type QuoteCurrency = typeof QUOTE_CURRENCIES[QuoteChar];

// Price level interface for buy/sell levels
export interface PriceLevel {
  price: number;
  volume: number;
}

// Main arbitrage opportunity interface
export interface ArbitrageOpportunity {
  // Exchange information
  buy_exchange: number;      // Index into EXCHANGES array (0-11)
  sell_exchange: number;     // Index into EXCHANGES array (0-11)

  // Trading pair information
  base: string;              // Base currency (e.g., "BTC", "ETH")
  buy_quote: QuoteChar;      // Buy side quote currency ('K', 'U', 'B')
  sell_quote: QuoteChar;     // Sell side quote currency ('K', 'U', 'B')

  // Price levels (sorted best to worst)
  buy_levels: PriceLevel[];  // Buy side price levels
  sell_levels: PriceLevel[]; // Sell side price levels

  // Conversion rate between quote currencies (always provided by backend)
  quote_conversion_rate: number; // 1.0 for direct arbitrage, actual rate for triangulated

  // Timestamps
  detected_at: number;       // When opportunity was first detected (ms)
  updated_at: number;        // Last update timestamp (ms)

  // Computed display properties (calculated once on arrival)
  buy_price?: number;        // Last buy level price (deepest level reached)
  sell_price?: number;       // Last sell level price (deepest level reached)
  max_volume?: number;       // Min of total buy/sell volumes (executable amount)
  profit_ratio?: number;     // Net profit ratio after fees (e.g., 1.05 = 5% profit)
  total_buy_value?: number;  // Last buy price × total buy volume
  buy_fee?: number;          // Buy side fee percentage
  sell_fee?: number;         // Sell side fee percentage
  total_fees?: number;       // Total fees in sell quote currency
  max_profitable_volume?: number; // Total budget calculated for BuyOpen strategies
}

// Helper to compute display properties with NET profit calculation
export function computeDisplayProperties(opp: ArbitrageOpportunity): ArbitrageOpportunity {
  // Ensure we have valid arrays
  if (!opp.buy_levels || !Array.isArray(opp.buy_levels) || opp.buy_levels.length === 0 ||
    !opp.sell_levels || !Array.isArray(opp.sell_levels) || opp.sell_levels.length === 0) {
    // Return the opportunity with default values if levels are missing
    return {
      ...opp,
      buy_price: opp.buy_price || 0,
      sell_price: opp.sell_price || 0,
      max_volume: opp.max_volume || 0,
      profit_ratio: opp.profit_ratio || 1,
      total_buy_value: opp.total_buy_value || 0,
      buy_fee: opp.buy_fee || 0,
      sell_fee: opp.sell_fee || 0,
      total_fees: opp.total_fees || 0
    };
  }

  // Buy and sell prices use LAST level (deepest level reached)
  const buy_price = opp.buy_levels[opp.buy_levels.length - 1]?.price || 0;
  const sell_price = opp.sell_levels[opp.sell_levels.length - 1]?.price || 0;

  // Get exchange names and quote currencies
  const buyExchange = getExchangeName(opp.buy_exchange);
  const sellExchange = getExchangeName(opp.sell_exchange);
  const buyQuote = getQuoteCurrency(opp.buy_quote);
  const sellQuote = getQuoteCurrency(opp.sell_quote);

  // Get fees (buy side uses taker, sell side uses maker for limit orders)
  const buyTakerFee = FeeStructure.getFee(buyExchange, 'taker', buyQuote);
  const sellMakerFee = FeeStructure.getFee(sellExchange, 'maker', sellQuote);

  // Calculate volumes considering buy fee reduces amount available to sell
  const buy_volume = opp.buy_levels.reduce((sum, level) => sum + level.volume, 0);
  const sell_volume = opp.sell_levels.reduce((sum, level) => sum + level.volume, 0);

  // After buying with taker fee, we receive less crypto
  const actual_volume_after_buy = buy_volume * (1 - buyTakerFee);

  // Maximum executable volume is the minimum of what we can buy and sell
  const max_executable_volume = Math.min(actual_volume_after_buy, sell_volume);

  // Calculate total cost to buy (sum of price * volume for all buy levels)
  const total_buy_cost = opp.buy_levels.reduce((sum, level) =>
    sum + (level.price * level.volume), 0
  );

  // Calculate sell revenue with reduced volume and maker fee
  // We need to apply the volume reduction and then the fee
  let remaining_volume = max_executable_volume;
  let gross_sell_revenue = 0;

  for (const level of opp.sell_levels) {
    const volume_at_level = Math.min(remaining_volume, level.volume);
    if (volume_at_level <= 0) break;

    gross_sell_revenue += level.price * volume_at_level;
    remaining_volume -= volume_at_level;
  }

  // Apply sell maker fee to get net revenue
  const net_sell_revenue = gross_sell_revenue * (1 - sellMakerFee);

  // Convert buy cost to sell quote currency for comparison
  const total_buy_cost_in_sell_quote = opp.quote_conversion_rate !== 1.0
    ? total_buy_cost * opp.quote_conversion_rate
    : total_buy_cost;

  // Calculate NET profit ratio (this is what we display as profit_ratio)
  const profit_ratio = total_buy_cost_in_sell_quote > 0
    ? (net_sell_revenue / total_buy_cost_in_sell_quote)
    : 0;

  // Calculate total fees for display
  const buy_fee_amount = total_buy_cost * buyTakerFee;
  const sell_fee_amount = gross_sell_revenue * sellMakerFee;
  const total_fees = buy_fee_amount + sell_fee_amount;

  // Total buy value: sum of (price × volume) for each level
  const total_buy_value = opp.buy_levels.reduce((sum, level) =>
    sum + (level.price * level.volume), 0
  );

  return {
    ...opp,
    buy_price,
    sell_price,
    max_volume: max_executable_volume,
    profit_ratio,  // This is now NET profit ratio
    total_buy_value,
    buy_fee: buyTakerFee,
    sell_fee: sellMakerFee,
    total_fees
  };
}

// Helper to get exchange name
export function getExchangeName(index: number): Exchange {
  return EXCHANGES[index] || 'Unknown' as Exchange;
}

// Helper to get quote currency string
export function getQuoteCurrency(char: QuoteChar): QuoteCurrency {
  return QUOTE_CURRENCIES[char];
}

// Quote currency symbol mapping
const QUOTE_SYMBOLS = {
  'K': '₩',  // Korean Won
  'U': '$',  // USDT (using dollar sign)
  'B': '₿'   // Bitcoin
} as const;

// Helper to get quote currency symbol
export function getQuoteCurrencySymbol(char: QuoteChar): string {
  return QUOTE_SYMBOLS[char];
}

// Quote currency colors
const QUOTE_COLORS = {
  'K': 'linear-gradient(180deg, #FF1744 0%, #FF1744 50%, #0066CC 50%, #0066CC 100%)', // Korean flag colors (vibrant red and bright blue for dark mode)
  'U': '#50AF95',  // Tether green (brighter for dark mode)
  'B': '#F7931A'   // Bitcoin orange
} as const;

// Helper to get quote currency color
export function getQuoteCurrencyColor(char: QuoteChar): string {
  return QUOTE_COLORS[char];
}

// Helper to generate cache key for opportunity
// Format matches Rust: [buy_exchange_enum][buy_quote_initial][base][sell_quote_initial][sell_exchange_enum]
export function makeOpportunityKey(opp: ArbitrageOpportunity): string {
  return `${opp.buy_exchange}${opp.buy_quote}${opp.base}${opp.sell_quote}${opp.sell_exchange}`;
}

// Opportunity lifecycle states
export const OpportunityLifecycle = {
  INSERT: 'INSERT',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE'
} as const;

export type OpportunityLifecycle = typeof OpportunityLifecycle[keyof typeof OpportunityLifecycle];

// WebSocket message type enum (for future use)
export const MessageType = {
  ARBITRAGE_OPPORTUNITY: 0,
  // Future message types...
} as const;

export type MessageType = typeof MessageType[keyof typeof MessageType];

// Snapshot types for starred opportunities
export interface OpportunitySnapshot {
  // Original opportunity data at time of snapshot
  opportunity: ArbitrageOpportunity;

  // Metadata
  snapshotTimestamp: number;      // When the snapshot was taken
  isStarred: boolean;              // Always true for snapshots

  // Tracking changes
  lastSeenTimestamp: number;       // Last time we saw this opportunity in updates
  isDeleted: boolean;              // True if opportunity no longer exists
}

// Track changes between snapshot and current state
export interface OpportunityChange {
  // Price changes
  buyPriceChange: number;          // Percentage change in buy price
  sellPriceChange: number;         // Percentage change in sell price

  // Volume changes  
  buyVolumeChange: number;         // Absolute change in buy volume
  sellVolumeChange: number;        // Absolute change in sell volume
  maxVolumeChange: number;         // Change in max executable volume

  // Profit changes
  profitRatioChange: number;       // Change in profit ratio (percentage points)

  // Status
  isDeleted: boolean;              // Opportunity no longer exists
  timeSinceSnapshot: number;       // Milliseconds since snapshot
}

// Enhanced opportunity for display (includes snapshot data)
export interface OpportunityWithSnapshot extends ArbitrageOpportunity {
  // Snapshot metadata
  isStarred?: boolean;
  snapshot?: OpportunitySnapshot;
  changes?: OpportunityChange;
}